import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useCrudItem } from "../../../hooks/useCrud"
import type { Agency, AgencyDetail, AgencyFormValues } from "../models/agency"
import { AgencyDetailSchema } from "../models/agency"
import { agencyApi } from "../services/agencyApi"

const BASE_KEY = "agencies"
const BASE_URL = "agency"

export function useAgency(id?: number | null) {
  return useCrudItem<AgencyDetail>(BASE_KEY, BASE_URL, AgencyDetailSchema, id)
}

export function useCreateAgency() {
  const queryClient = useQueryClient()
  return useMutation<AgencyDetail, Error, AgencyFormValues>({
    mutationFn: (values) => agencyApi.create(values) as Promise<AgencyDetail>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useUpdateAgency() {
  const queryClient = useQueryClient()
  return useMutation<AgencyDetail, Error, { id: number; values: Partial<AgencyFormValues> }>({
    mutationFn: ({ id, values }) => agencyApi.update(id, values) as Promise<AgencyDetail>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useUploadMultimedia() {
  const queryClient = useQueryClient()
  return useMutation<AgencyDetail, Error, { id: number; type: string; file: File }>({
    mutationFn: ({ id, type, file }) => agencyApi.uploadMultimedia(id, type, file) as Promise<AgencyDetail>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useRemoveMultimedia() {
  const queryClient = useQueryClient()
  return useMutation<AgencyDetail, Error, { id: number; type: string }>({
    mutationFn: ({ id, type }) => agencyApi.removeMultimedia(id, type) as Promise<AgencyDetail>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useDeleteAgency() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, number>({
    mutationFn: (id) => agencyApi.delete(id) as Promise<void>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useMasterAgencies() {
  return useQuery<Agency[]>({
    queryKey: [BASE_KEY, "master"],
    queryFn: async () => {
      const { data } = await agencyApi.getList({ is_master: true, page_size: 200 })
      return data?.items ?? []
    },
    staleTime: 1000 * 60 * 30,
  })
}
